import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ChevronsUpDown } from "lucide-react"
import * as React from "react"
import { useProductList } from "../../../api/productsApi"

interface ProductPickerProps {
  /** Label shown on the trigger — the currently chosen product, or a prompt. */
  triggerLabel: string
  onSelect: (productId: string, label: string) => void
  className?: string
}

/**
 * Searchable single-select product combobox, filtered by product number/name
 * server-side against the org's product master data — modeled on
 * SubstancePicker.tsx. No "load more": same reasoning as ArticlePicker, the
 * server-side search already narrows the 50-row page enough that a second
 * page is rarely needed.
 */
export function ProductPicker({
  triggerLabel,
  onSelect,
  className,
}: ProductPickerProps) {
  const { translate } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [q, setQ] = React.useState("")
  const debouncedQ = useDebouncedValue(q, 250)

  const {
    data: productPages,
    isFetching,
    isPlaceholderData,
  } = useProductList({
    q: debouncedQ || undefined,
    limit: 50,
  })

  // `q` differs from `debouncedQ` during the debounce window, and
  // `isPlaceholderData` stays true while the request for the now-current
  // `debouncedQ` is still in flight (keepPreviousData renders the previous
  // search's rows meanwhile) — either way the rendered rows don't match `q`.
  const isStale = q !== debouncedQ || isPlaceholderData

  const products = React.useMemo(
    () => productPages?.pages.flatMap((p) => p.items) ?? [],
    [productPages],
  )

  const handleSelect = React.useCallback(
    (productId: string) => {
      if (isStale) return
      const product = products.find((p) => p.id === productId)
      if (!product) return
      onSelect(product.id, `${product.productNumber} ${product.name}`)
      setOpen(false)
      setQ("")
    },
    [products, onSelect, isStale],
  )

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) setQ("")
  }, [])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex h-9 w-full items-center justify-between gap-1 rounded-md border border-grey4 bg-grey2 px-3 text-[13px] text-white hover:bg-primary/4",
            className,
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown size={13} className="shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={translate("MC_MD_LINK_ARTICLE_SEARCH")}
            value={q}
            onValueChange={setQ}
          />
          <CommandList className="max-h-[260px]">
            {!isFetching && products.length === 0 && (
              <CommandEmpty>
                {translate("MC_MD_LINK_ARTICLE_SEARCH_NONE")}
              </CommandEmpty>
            )}
            {products.length > 0 && (
              <CommandGroup className="overflow-y-auto">
                {products.map((product) => (
                  <CommandItem
                    key={product.id}
                    value={product.id}
                    onSelect={handleSelect}
                    className="text-[13px]"
                  >
                    <span className="font-mono">{product.productNumber}</span>
                    <span className="ml-1.5 truncate text-grey5">
                      {product.name}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
